import * as React from "react";
import { defaultsAll, keys, toPairs, values } from "lodash/fp";
import { normalizeElementId } from "../../utils/normalizeElementId";
import { formatSVGReference, getSVGReferenceType } from "../../spinoffs/formatSVGReference";
import * as filterDrawerMap from "../../drawers/filters/index";
export const getSVGFilterReferenceType = (filterName) => {
    return getSVGReferenceType(filterName, ["string", "localIRI", "nonLocalIRI"]);
};
export function getNamespacedId(id) {
    return normalizeElementId("kaavio" + id);
}
export function getNamespacedFilter(filterProps) {
    const { filterName } = filterProps;
    return filterDrawerMap[filterName](Object.assign({ getNamespacedId }, filterProps));
}
export function getNamespacedFilterId(filterProps) {
    const { filterName } = filterProps;
    const svgReferenceType = getSVGFilterReferenceType(filterName);
    if (svgReferenceType === "localIRI") {
        // We can only tweak the color, stroke width, etc. for filters that are
        // located in this SVG (referenced via local IRIs)
        return getNamespacedFilter(filterProps).filterProperties.id;
    }
    else {
        return filterName;
    }
}
/*
// Isn't the following the same as the one below?
export function getFilterReference1(filterProps) {
  const namespacedFilterId = getNamespacedFilterId(filterProps);
  return `url(#${namespacedFilterId})`;
}
//*/
/* Returns a filter reference like "url(file.svg#filter-element-id)"
 * See
 * https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/filter
 * https://developer.mozilla.org/en-US/docs/Web/CSS/filter
 */
export function getFilterReference({ color, filterName, fill, strokeWidth, parentFill }) {
    const svgReferenceType = getSVGFilterReferenceType(filterName);
    if (svgReferenceType === "string") {
        // Don't make a FuncIRI out of a string value
        return filterName;
    }
    else if (svgReferenceType === "nonLocalIRI") {
        // We can't set the color, etc. for a non-local IRI
        return formatSVGReference(filterName, [svgReferenceType]);
    }
    const namespacedFilterId = getNamespacedFilterId({
        color,
        filterName,
        fill,
        strokeWidth,
        parentFill
    });
    return formatSVGReference(namespacedFilterId, [svgReferenceType]);
}
export class FilterDefs extends React.Component {
    constructor(props) {
        super(props);
        const { entitiesById, highlights, pathway } = props;
        const { height, width } = pathway;
        const entityValues = values(entitiesById);
        const definedFromStrokeStyleDouble = entityValues
            .filter(entity => entity.hasOwnProperty("strokeStyle") &&
            entity.strokeStyle === "double")
            .reduce(function (acc, entity) {
            const { filterProperties, filterPrimitives } = getNamespacedFilter(Object.assign({ filterName: "Double" }, entity));
            acc[filterProperties.id] = { filterProperties, filterPrimitives };
            return acc;
        }, {});
        const definedFromEntityFilterProperties = entityValues
            .filter(entity => entity.hasOwnProperty("filters"))
            .reduce(function (acc, entity) {
            entity.filters
                // NOTE: yes, this is funny, but SVG filter is different from JS filter
                .filter(filterName => getSVGFilterReferenceType(filterName) === "localIRI")
                .forEach(function (filterName) {
                const { filterProperties, filterPrimitives } = getNamespacedFilter(Object.assign({ filterName }, entity));
                acc[filterProperties.id] = { filterProperties, filterPrimitives };
            });
            return acc;
        }, {});
        const definedFromHighlighted = highlights.reduce(function (acc, [targetKey, targetValue, styleValue]) {
            const filterName = "Highlight";
            const { filterProperties, filterPrimitives } = getNamespacedFilter({
                color: styleValue,
                filterName
            });
            acc[filterProperties.id] = { filterProperties, filterPrimitives };
            return acc;
        }, {});
        const definedFromBlackAndWhiteToColor = keys(entityValues
            // TODO should we filter out "white", "#fff", "#ffffff", "black", "#000", "#000000"?
            // would I need to make sure I'm not referencing those filters somewhere?
            .filter(entity => entity.color || entity.fill || entity.stroke)
            .reduce(function (acc, entity) {
            [entity.color, entity.fill, entity.stroke].forEach(function (colorFillOrStroke) {
                acc[colorFillOrStroke] = true;
            });
            return acc;
        }, {})).reduce(function (acc, colorFillOrStroke) {
            ["WhiteToColor", "BlackToColor"].forEach(function (filterName) {
                const filterResponse = getNamespacedFilter({
                    color: colorFillOrStroke,
                    filterName
                });
                if (filterResponse !== "none") {
                    const { filterProperties, filterPrimitives } = filterResponse;
                    acc[filterProperties.id] = {
                        // TODO file a bug report on this. Observed in both Chrome and FF.
                        // Paths don't get the filter property applied properly, because
                        // when the SVG height/width=inherit, somehow the filter thinks
                        // the total size of the SVG is smaller than it actually is when
                        // the browser window is small.
                        filterProperties: Object.assign({ x: 0, y: 0, height,
                            width }, filterProperties),
                        //filterProperties,
                        filterPrimitives
                    };
                }
            });
            return acc;
        }, {});
        this.state = {
            defined: defaultsAll([
                definedFromEntityFilterProperties,
                definedFromStrokeStyleDouble,
                definedFromHighlighted,
                definedFromBlackAndWhiteToColor
            ])
        };
    }
    /* If the diagram is updated after the initial render, this step will handle
     * the case of needing a filter definition that wasn't defined in the
     * constructor, such as if a user were to add a new group with a fill (background
     * color) that was not present initially and then dragged a filter on top of a
     * type of that group. This doesn't do anything on server-side rendering.
     */
    // TODO re-enable this, but take into account potential clobbering of id globally
    //  componentWillReceiveProps(nextProps: FilterDefsProps) {
    //    const { state, props } = this;
    //    const { defined } = state;
    //    const { latestFilterReferenced } = nextProps;
    //    const definedIds = keys(defined);
    //    if (!isEmpty(latestFilterReferenced)) {
    //      const {
    //        fill,
    //        strokeWidth,
    //        color,
    //        filterName,
    //        parentFill
    //      } = latestFilterReferenced;
    //
    //      if (isEmpty(filterName)) {
    //        throw new Error(`Missing filterName`);
    //      }
    //
    //      const svgReferenceType = getSVGFilterReferenceType(filterName);
    //
    //      if (svgReferenceType === "localIRI") {
    //        // We can only tweak the color, stroke width, etc. for filters that are
    //        // located in this SVG (referenced via local IRIs)
    //
    //        const { filterProperties, filterPrimitives } = getNamespacedFilter({
    //          fill,
    //          strokeWidth,
    //          color,
    //          filterName,
    //          parentFill
    //        });
    //
    //        const id = filterProperties.id;
    //
    //        if (definedIds.indexOf(id) === -1) {
    //          defined[id] = { filterProperties, filterPrimitives };
    //          this.setState({
    //            defined
    //          });
    //        }
    //      }
    //    }
    //  }
    render() {
        const { defined } = this.state;
        return (React.createElement("g", { id: "jit-defs" }, toPairs(defined).map(([filterId, { filterProperties, filterPrimitives }]) => {
            return (React.createElement("filter", Object.assign({ key: filterId }, filterProperties), filterPrimitives));
        })));
    }
}
//# sourceMappingURL=data:application/json;base64,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